import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, TrendingUp, Users, DollarSign } from "lucide-react";
import { toast } from "sonner";
import DatePickerInput from "@/components/DatePickerInput";

const PerformanceReportPanel = ({ user }) => {
  const navigate = useNavigate();
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Default: today only (avoid loading all data at once)
  const todayStr = new Date().toLocaleDateString("en-CA");
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  useEffect(() => {
    fetchPerformance();
  }, []);

  const fetchPerformance = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Convert date to start of day and end of day in ISO format
      if (startDate) {
        const startDateTime = new Date(startDate + 'T00:00:00');
        params.append("start_date", startDateTime.toISOString());
      }
      if (endDate) {
        const endDateTime = new Date(endDate + 'T23:59:59');
        params.append("end_date", endDateTime.toISOString());
      }

      const response = await axiosInstance.get("/reports/performance?" + params.toString());
      setPerformanceData(response.data);
    } catch (error) {
      console.error("Error fetching performance:", error);
      toast.error(error.response?.data?.detail || "Gagal memuat data performance");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchPerformance();
  };

  const handlePresetDate = async (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case "today":
        start = end = new Date();
        break;
      case "yesterday":
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = yesterday;
        break;
      case "this_week":
        const now = new Date();
        const currentDay = now.getDay();
        const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
        start = new Date(now);
        start.setDate(now.getDate() + mondayOffset);
        end = new Date();
        break;
      case "last_week":
        const thisWeekStart = new Date();
        const thisDayOfWeek = thisWeekStart.getDay();
        const daysToMonday = thisDayOfWeek === 0 ? -6 : 1 - thisDayOfWeek;
        thisWeekStart.setDate(thisWeekStart.getDate() + daysToMonday);
        
        end = new Date(thisWeekStart);
        end.setDate(thisWeekStart.getDate() - 1);
        start = new Date(end);
        start.setDate(end.getDate() - 6);
        break;
      case "this_month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date();
        break;
      case "last_month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        return;
    }

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const startDateStr = formatDate(start);
    const endDateStr = formatDate(end);

    setStartDate(startDateStr);
    setEndDate(endDateStr);
    
    // Fetch immediately with the calculated dates
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      const startDateTime = new Date(startDateStr + 'T00:00:00');
      params.append("start_date", startDateTime.toISOString());
      
      const endDateTime = new Date(endDateStr + 'T23:59:59');
      params.append("end_date", endDateTime.toISOString());

      const response = await axiosInstance.get("/reports/performance?" + params.toString());
      setPerformanceData(response.data);
    } catch (error) {
      console.error("Error fetching performance:", error);
      toast.error(error.response?.data?.detail || "Gagal memuat data performance");
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setStartDate("");
    setEndDate("");
    setTimeout(() => fetchPerformance(), 100);
  };

  // Separate marketing and follow up data
  const marketingData = performanceData.filter(p => p.role === "marketing");
  const followUpData = performanceData.filter(p => p.role === "follow_up");

  // Calculate totals
  const totalMarketingRegis = marketingData.reduce((sum, p) => sum + p.total_regis, 0);
  const totalMarketingNDP = marketingData.reduce((sum, p) => sum + p.total_ndp, 0);
  const totalMarketingDepo = marketingData.reduce((sum, p) => sum + p.total_depo, 0);

  const totalFollowUpNDP = followUpData.reduce((sum, p) => sum + p.total_ndp, 0);
  const totalFollowUpDepo = followUpData.reduce((sum, p) => sum + p.total_depo, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f10] via-[#1a1a1b] to-[#0f0f10]" data-testid="performance-report-panel">
      <header className="bg-[#1a1a1b]/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/dashboard")}
              variant="ghost"
              size="sm"
              className="text-gray-300 hover:text-white hover:bg-red-900/20"
            >
              <ArrowLeft className="mr-2" size={18} />
              Kembali
            </Button>
            <h1 className="text-2xl font-bold text-red-50">Performance Review</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter */}
        <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30 mb-6">
          <CardHeader>
            <CardTitle className="text-red-50">Filter Periode</CardTitle>
            <CardDescription className="text-gray-400">Pilih rentang tanggal untuk melihat performance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="perf-start-date" className="text-gray-300">Dari Tanggal</Label>
                <DatePickerInput
                  id="perf-start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-[#0f0f10] border-red-900/30 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="perf-end-date" className="text-gray-300">Sampai Tanggal</Label>
                <DatePickerInput
                  id="perf-end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-[#0f0f10] border-red-900/30 text-white"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleApplyFilters}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                >
                  Terapkan
                </Button>
                <Button
                  onClick={handleResetFilters}
                  variant="outline"
                  className="border-red-900/30 text-white hover:bg-red-900/20"
                >
                  Reset
                </Button>
              </div>
            </div>

            {/* Date Preset Buttons */}
            <div className="mt-4">
              <Label className="text-gray-300 mb-2 block">Preset Tanggal</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => handlePresetDate("today")}
                  variant="outline"
                  size="sm"
                  className="border-red-900/30 text-white hover:bg-red-900/20"
                >
                  Hari Ini
                </Button>
                <Button
                  onClick={() => handlePresetDate("yesterday")}
                  variant="outline"
                  size="sm"
                  className="border-red-900/30 text-white hover:bg-red-900/20"
                >
                  Kemarin
                </Button>
                <Button
                  onClick={() => handlePresetDate("this_week")}
                  variant="outline"
                  size="sm"
                  className="border-red-900/30 text-white hover:bg-red-900/20"
                >
                  Minggu Ini
                </Button>
                <Button
                  onClick={() => handlePresetDate("last_week")}
                  variant="outline"
                  size="sm"
                  className="border-red-900/30 text-white hover:bg-red-900/20"
                >
                  Minggu Lalu
                </Button>
                <Button
                  onClick={() => handlePresetDate("this_month")}
                  variant="outline"
                  size="sm"
                  className="border-red-900/30 text-white hover:bg-red-900/20"
                >
                  Bulan Ini
                </Button>
                <Button
                  onClick={() => handlePresetDate("last_month")}
                  variant="outline"
                  size="sm"
                  className="border-red-900/30 text-white hover:bg-red-900/20"
                >
                  Bulan Lalu
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
          </div>
        ) : (
          <>
            {/* Marketing Performance */}
            <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30 mb-6">
              <CardHeader>
                <CardTitle className="text-red-50">Performance Marketing</CardTitle>
                <CardDescription className="text-gray-400">
                  Total Regis: {totalMarketingRegis} | Total NDP: {totalMarketingNDP} | Total Depo: Rp {totalMarketingDepo.toLocaleString('id-ID')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {marketingData.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">Tidak ada data marketing</div>
                ) : (
                  <div className="rounded-lg border border-red-900/30 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#0f0f10] border-red-900/30 hover:bg-[#0f0f10]">
                          <TableHead className="text-red-400 font-semibold">Nama Marketing</TableHead>
                          <TableHead className="text-red-400 font-semibold text-center">Jumlah Regis</TableHead>
                          <TableHead className="text-red-400 font-semibold text-center">Jumlah NDP</TableHead>
                          <TableHead className="text-red-400 font-semibold text-center">In Review</TableHead>
                          <TableHead className="text-red-400 font-semibold text-right">Total Depo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {marketingData.map((data) => (
                          <TableRow key={data.name} className="border-red-900/20 hover:bg-[#0f0f10]/50">
                            <TableCell className="font-medium text-white">{data.name}</TableCell>
                            <TableCell className="text-yellow-400 text-center font-semibold">{data.total_regis}</TableCell>
                            <TableCell className="text-green-400 text-center font-semibold">{data.total_ndp}</TableCell>
                            <TableCell className="text-blue-400 text-center font-semibold">{data.total_in_review || 0}</TableCell>
                            <TableCell className="text-green-400 text-right font-semibold">Rp {data.total_depo.toLocaleString('id-ID')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Follow Up Performance */}
            <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
              <CardHeader>
                <CardTitle className="text-red-50">Performance Follow Up</CardTitle>
                <CardDescription className="text-gray-400">
                  Total NDP Closed: {totalFollowUpNDP} | Total Depo: Rp {totalFollowUpDepo.toLocaleString('id-ID')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {followUpData.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">Tidak ada data follow up</div>
                ) : (
                  <div className="rounded-lg border border-red-900/30 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#0f0f10] border-red-900/30 hover:bg-[#0f0f10]">
                          <TableHead className="text-red-400 font-semibold">Nama Follow Up</TableHead>
                          <TableHead className="text-red-400 font-semibold text-center">Jumlah NDP (Closed)</TableHead>
                          <TableHead className="text-red-400 font-semibold text-center">In Review</TableHead>
                          <TableHead className="text-red-400 font-semibold text-right">Total Depo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {followUpData.map((data) => (
                          <TableRow key={data.name} className="border-red-900/20 hover:bg-[#0f0f10]/50">
                            <TableCell className="font-medium text-white">{data.name}</TableCell>
                            <TableCell className="text-green-400 text-center font-semibold">{data.total_ndp}</TableCell>
                            <TableCell className="text-blue-400 text-center font-semibold">{data.total_in_review || 0}</TableCell>
                            <TableCell className="text-green-400 text-right font-semibold">Rp {data.total_depo.toLocaleString('id-ID')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default PerformanceReportPanel;